import React, { useEffect, useState } from 'react';
import { useSystem } from '../../contexts/SystemContext';
import { clearAllCaches, warmPublicCaches, resetFailedJobs } from '../../features/admin/systemActions';
import { useAuth } from '../../hooks/useAuth';

export function ManualTab() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    isProcessing,
    fetchSystemData,
    triggerNewsScraping,
    triggerRiskCalculation
  } = useSystem();
  
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  // Fetch system data and pending backfills when component mounts
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchSystemData();
      (async () => {
        try {
          const api = (await import('../../services/api')).default;
          const c = await api.getBackfillPending();
          setPendingCount(c);
        } catch {}
      })();
    }
  }, [isAuthenticated, authLoading, fetchSystemData]);

  const handleTriggerScraping = async () => {
    try {
      setLastAction('Triggering news scraping...');
      setActionResult(null);
      await triggerNewsScraping();
      setActionResult({ type: 'success', message: 'News scraping triggered successfully!' });
      setLastAction(null);
    } catch (error) {
      setActionResult({ type: 'error', message: 'Failed to trigger news scraping.' });
      setLastAction(null);
    }
  };

  const handleTriggerRiskCalculation = async () => {
    try {
      setLastAction('Triggering risk calculation...');
      setActionResult(null);
      await triggerRiskCalculation();
      setActionResult({ type: 'success', message: 'Risk calculation triggered successfully!' });
      setLastAction(null);
    } catch (error) {
      setActionResult({ type: 'error', message: 'Failed to trigger risk calculation.' });
      setLastAction(null);
    }
  };

  const handleBackfillRiskAll = async () => {
    try {
      setLastAction('Enqueuing risk backfill for all days...');
      setActionResult(null);
      const api = (await import('../../services/api')).default;
      const res = await api.triggerRiskBackfillRange();
      setActionResult({ type: 'success', message: `Enqueued risk backfill for ${res.enqueued} day(s)` });
      setLastAction(null);
    } catch (error) {
      setActionResult({ type: 'error', message: 'Failed to enqueue risk backfill.' });
      setLastAction(null);
    }
  };

  const handleHealthCheck = () => {
    setActionResult({ type: 'success', message: 'Health check completed. All systems operational.' });
  };

  const handleClearCache = async () => {
    try {
      setLastAction('Clearing caches...');
      setActionResult(null);
      const msg = await clearAllCaches();
      setLastAction('Warming public caches...');
      await warmPublicCaches('EST');
      setActionResult({ type: 'success', message: `✅ ${msg} and warmed.` });
    } catch (e) {
      console.error('Failed to clear/warm caches', e);
      setActionResult({ type: 'error', message: '❌ Failed to clear/warm caches.' });
    } finally {
      setLastAction(null);
    }
  };

  const handleResetFailedJobs = async () => {
    try {
      setLastAction('Resetting failed jobs...');
      setActionResult(null);
      const res = await resetFailedJobs(true);
      setActionResult({ type: 'success', message: `Reset jobs: failed=${res.failedReset}, stuck=${res.stuckReset}` });
    } catch (e) {
      console.error('Failed to reset jobs', e);
      setActionResult({ type: 'error', message: '❌ Failed to reset jobs.' });
    } finally {
      setLastAction(null);
    }
  };

  const handleExportLogs = () => {
    setActionResult({ type: 'success', message: 'Log export is not implemented yet.' });
  };

  // Translation Backfill UI state
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState<null | { scanned: number; updated: number; skipped: number; errors: string[]; languagesUsed?: string[] }>(null);
  const [dryRun, setDryRun] = useState(false);
  const [startAfterId, setStartAfterId] = useState<string>('');
  const [budgetCap, setBudgetCap] = useState<string>('');
  const [pendingCount, setPendingCount] = useState<{ totalAnalyses: number; pending: number; complete: number; languages: string[] } | null>(null);

  const runBackfill = async (count: number) => {
    try {
      setBackfillRunning(true);
      setBackfillResult(null);
      const payload: any = {
        // Use countries-configured languages by default (server-side)
        languages: undefined,
        limit: count,
        dryRun,
        startAfterId: startAfterId ? parseInt(startAfterId, 10) : undefined,
        budgetCapUsd: budgetCap ? parseFloat(budgetCap) : undefined,
        // Backfill all configured languages; do not restrict to new-only by default
        onlyNewLanguages: undefined,
      };
      const res = await (await import('../../services/api')).default.backfillTranslations(payload);
      setBackfillResult(res);
      setActionResult({ type: 'success', message: `Backfill complete. Updated: ${res.updated}, Skipped: ${res.skipped}` });
    } catch (e) {
      setActionResult({ type: 'error', message: 'Backfill failed. See console for details.' });
      console.error(e);
    } finally {
      setBackfillRunning(false);
      try {
        const api = (await import('../../services/api')).default;
        const c = await api.getBackfillPending();
        setPendingCount(c);
      } catch {}
    }
  };

  // Background job
  const [jobId, setJobId] = useState<number | null>(null);
  const [jobStatus, setJobStatus] = useState<any | null>(null);
  useEffect(() => {
    let t: any;
    if (jobId) {
      const poll = async () => {
        try {
          const resp = await (await import('../../services/api')).default.get(`/api/admin/testing/backfill/job/${jobId}`);
          setJobStatus(resp.data);
          const status = resp.data?.status || resp.data?.Status;
          if (status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED') {
            // refresh counts once when job settles
            const api = (await import('../../services/api')).default;
            try { const c = await api.getBackfillPending(); setPendingCount(c); } catch {}
          }
        } catch {}
      };
      poll();
      t = setInterval(poll, 3000);
    }
    return () => t && clearInterval(t);
  }, [jobId]);

  const queueBackfill = async (count: number) => {
    try {
      setBackfillRunning(true);
      const payload: any = {
        languages: undefined,
        limit: count,
        startAfterId: startAfterId ? parseInt(startAfterId, 10) : undefined,
        budgetCapUsd: budgetCap ? parseFloat(budgetCap) : undefined,
        onlyNewLanguages: undefined,
      };
      const resp = await (await import('../../services/api')).default.post('/api/admin/testing/backfill/translations/queue', payload);
      setJobId(resp.data.jobId);
      setActionResult({ type: 'success', message: `Queued backfill job #${resp.data.jobId}` });
    } catch (e) {
      setActionResult({ type: 'error', message: 'Failed to queue backfill job.' });
    } finally {
      setBackfillRunning(false);
      try {
        const api = (await import('../../services/api')).default;
        const c = await api.getBackfillPending();
        setPendingCount(c);
      } catch {}
    }
  };

  const cancelJob = async () => {
    if (!jobId) return;
    try {
      await (await import('../../services/api')).default.post(`/api/admin/testing/backfill/job/${jobId}/cancel`);
      setActionResult({ type: 'success', message: `Cancel requested for job #${jobId}` });
    } catch (e) {
      setActionResult({ type: 'error', message: 'Failed to cancel job.' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Result Alert */}
      {actionResult && (
        <div className={`tc-card p-4 border-l-4 ${
          actionResult.type === 'success' 
            ? 'border-green-400 bg-green-50' 
            : 'border-red-400 bg-red-50'
        }`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className={`text-xl ${
                actionResult.type === 'success' ? 'text-green-600' : 'text-red-600'
              }`}>
                {actionResult.type === 'success' ? '✅' : '❌'}
              </span>
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                actionResult.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {actionResult.message}
              </p>
            </div>
            <div className="ml-auto">
              <button
                onClick={() => setActionResult(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Action Status */}
      {(lastAction || isProcessing) && (
        <div className="tc-card p-4 border-l-4 border-blue-400 bg-blue-50">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="tc-spinner w-5 h-5"></div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-800">
                {lastAction || 'Processing operation...'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Manual Triggers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="tc-card tc-card-hover p-6">
          <h3 className="text-xl font-semibold mb-4 tc-text-primary">Manual Triggers</h3>
          <div className="space-y-4">
            <button
              onClick={handleTriggerScraping}
              disabled={isProcessing}
              className="w-full tc-btn tc-btn-primary disabled:opacity-50"
            >
              {isProcessing ? '🔄 Processing...' : '📡 Trigger All News Scraping'}
            </button>
            
            <button
              onClick={handleTriggerRiskCalculation}
              disabled={isProcessing}
              className="w-full tc-btn tc-btn-primary disabled:opacity-50"
            >
              {isProcessing ? '🔄 Processing...' : '📊 Calculate Daily Risks'}
            </button>
            
            <button
              onClick={handleBackfillRiskAll}
              disabled={isProcessing}
              className="w-full tc-btn tc-btn-secondary disabled:opacity-50"
            >
              {isProcessing ? '🔄 Processing...' : '📅 Backfill Risks (All Days)'}
            </button>
            
            <button
              onClick={handleHealthCheck}
              disabled={isProcessing}
              className="w-full tc-btn tc-btn-secondary disabled:opacity-50"
            >
              🏥 Run Health Check
            </button>
          </div>
          
          <div className="mt-4 p-3 tc-bg-secondary rounded-lg">
            <p className="text-xs tc-text-tertiary">
              Manual triggers are useful for testing and immediate operations. 
              Normal operations run automatically via the scheduler.
            </p>
          </div>
        </div>

        <div className="tc-card tc-card-hover p-6">
          <h3 className="text-xl font-semibold mb-4 tc-text-primary">System Actions</h3>
          <div className="space-y-4">
            <button
              onClick={handleClearCache}
              disabled={isProcessing}
              className="w-full tc-btn tc-btn-secondary disabled:opacity-50"
            >
              🗑️ Clear Cache
            </button>
            
            <button
              onClick={handleResetFailedJobs}
              disabled={isProcessing}
              className="w-full tc-btn tc-btn-secondary disabled:opacity-50"
            >
              🔄 Reset Failed Jobs
            </button>
            
            <button
              onClick={handleExportLogs}
              disabled={isProcessing}
              className="w-full tc-btn tc-btn-secondary disabled:opacity-50"
            >
              📋 Export System Logs
            </button>
          </div>
          
          <div className="mt-4 p-3 tc-bg-secondary rounded-lg">
            <p className="text-xs tc-text-tertiary">
              System maintenance actions. Some features are not yet implemented 
              but will be available in future updates.
            </p>
          </div>
        </div>

        <div className="tc-card tc-card-hover p-6">
          <h3 className="text-xl font-semibold mb-4 tc-text-primary">Translation Backfill</h3>
          <p className="tc-text-secondary text-sm mb-3">Backfills missing summaries and key points for all languages configured in Countries.</p>
          {pendingCount && (
            <div className="flex items-center gap-3 text-sm tc-text-secondary mb-2">
              <div>
                Pending backfills: {pendingCount.pending} of {pendingCount.totalAnalyses} • Complete: {pendingCount.complete} • Languages: {pendingCount.languages.join(', ')}
              </div>
              <button
                className="tc-btn tc-btn-xs tc-btn-secondary"
                onClick={async () => {
                  try {
                    const api = (await import('../../services/api')).default;
                    const c = await api.getBackfillPending();
                    setPendingCount(c);
                  } catch {}
                }}
              >Refresh</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <label className="block">
              <span className="block text-xs tc-text-tertiary mb-1">Start After Analysis ID (optional)</span>
              <input className="tc-input w-full" value={startAfterId} onChange={(e) => setStartAfterId(e.target.value)} placeholder="e.g. 12000" />
            </label>
            <label className="block">
              <span className="block text-xs tc-text-tertiary mb-1">Budget Cap USD (optional)</span>
              <input className="tc-input w-full" value={budgetCap} onChange={(e) => setBudgetCap(e.target.value)} placeholder="e.g. 2.50" />
            </label>
            <label className="flex items-end gap-2">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              <span className="tc-text-secondary text-sm">Dry run (no writes)</span>
            </label>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => runBackfill(5)} className="tc-btn tc-btn-primary" disabled={backfillRunning}>Backfill 5</button>
            <button onClick={() => runBackfill(25)} className="tc-btn tc-btn-primary" disabled={backfillRunning}>Backfill 25</button>
            <button onClick={() => runBackfill(100)} className="tc-btn tc-btn-primary" disabled={backfillRunning}>Backfill 100</button>
            <button onClick={() => queueBackfill(25)} className="tc-btn tc-btn-secondary" disabled={backfillRunning}>Run in Background (25)</button>
            <button onClick={async () => {
              try {
                setBackfillRunning(true);
                const resp = await (await import('../../services/api')).default.post('/api/admin/testing/backfill/translations/queue', {
                  all: true,
                  budgetCapUsd: budgetCap ? parseFloat(budgetCap) : undefined,
                });
                setJobId(resp.data.jobId);
                setActionResult({ type: 'success', message: `Queued full backfill job #${resp.data.jobId}` });
              } catch (e) {
                setActionResult({ type: 'error', message: 'Failed to queue full backfill job.' });
              } finally {
                setBackfillRunning(false);
              }
            }} className="tc-btn tc-btn-secondary" disabled={backfillRunning}>Backfill All (queue)</button>
          </div>
          {backfillRunning && (
            <div className="mt-3 text-sm tc-text-secondary">Running backfill...</div>
          )}
          {backfillResult && (
            <div className="mt-4 p-3 tc-bg-secondary rounded">
              <div className="text-sm tc-text-secondary">Scanned: {backfillResult.scanned}</div>
              <div className="text-sm tc-text-secondary">Updated: {backfillResult.updated}</div>
              <div className="text-sm tc-text-secondary">Skipped: {backfillResult.skipped}</div>
              {backfillResult.languagesUsed && backfillResult.languagesUsed.length > 0 && (
                <div className="text-sm tc-text-secondary">Languages: {backfillResult.languagesUsed.join(', ')}</div>
              )}
              {backfillResult.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer tc-text-secondary">Errors ({backfillResult.errors.length})</summary>
                  <ul className="text-xs list-disc ml-5 mt-2">
                    {backfillResult.errors.slice(0, 5).map((e, i) => (<li key={i}>{e}</li>))}
                  </ul>
                </details>
              )}
            </div>
          )}
          {jobId && (
            <div className="mt-4 p-3 tc-bg-secondary rounded">
              <div className="text-sm tc-text-secondary">Job: #{jobId}</div>
              <div className="text-sm tc-text-secondary">Status: {jobStatus?.status || jobStatus?.Status || '...'}</div>
              {jobStatus?.payload?.progress && (
                <div className="text-sm tc-text-secondary mt-1">
                  Progress: scanned {jobStatus.payload.progress.scanned ?? 0}, updated {jobStatus.payload.progress.updated ?? 0}, skipped {jobStatus.payload.progress.skipped ?? 0}, cost ${jobStatus.payload.progress.cost?.toFixed ? jobStatus.payload.progress.cost.toFixed(4) : jobStatus.payload.progress.cost}
                </div>
              )}
              <div className="mt-2">
                <button onClick={cancelJob} className="tc-btn tc-btn-secondary text-sm">Cancel Job</button>
              </div>
              {jobStatus?.results?.length > 0 && (
                <pre className="mt-2 text-xs overflow-auto max-h-48">{JSON.stringify(jobStatus.results[jobStatus.results.length - 1]?.result || jobStatus.results[0]?.result, null, 2)}</pre>
              )}
            </div>
          )}
          <div className="mt-4 p-3 tc-bg-secondary rounded-lg">
            <p className="text-xs tc-text-tertiary">First run a small batch to validate results, then increase. Uses tier‑1 model for translations to control cost.</p>
          </div>
        </div>
      </div>

      {/* Operation Guidelines */}
      <div className="tc-card p-6">
        <h3 className="text-lg font-medium tc-text-primary mb-4">Operation Guidelines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium tc-text-primary mb-2">News Scraping</h4>
            <ul className="text-sm tc-text-secondary space-y-1">
              <li>• Fetches articles from all active RSS sources</li>
              <li>• Automatically queues articles for AI analysis</li>
              <li>• Runs hourly via scheduler normally</li>
              <li>• Manual trigger useful for immediate updates</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium tc-text-primary mb-2">Risk Calculation</h4>
            <ul className="text-sm tc-text-secondary space-y-1">
              <li>• Calculates daily risk scores and trends</li>
              <li>• Processes all analyzed articles</li>
              <li>• Runs daily at 2 AM normally</li>
              <li>• Manual trigger for testing scenarios</li>
            </ul>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="tc-card p-6">
        <h3 className="text-lg font-medium tc-text-primary mb-4">System Information</h3>
        <div className="prose prose-sm tc-text-secondary">
          <ul className="space-y-2">
            <li><strong>Scheduler:</strong> Automated operations run on configured intervals</li>
            <li><strong>Queue System:</strong> PostgreSQL-based job queue, no Redis dependency</li>
            <li><strong>Processing:</strong> Jobs processed every 5 seconds with retry logic</li>
            <li><strong>Monitoring:</strong> Health checks every 5 minutes, stuck job detection</li>
            <li><strong>Cost Control:</strong> Daily budget monitoring prevents runaway costs</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
